import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search,
  MapPin,
  Briefcase,
  GraduationCap,
  Users,
  Sparkles,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Building2,
  Monitor,
  Heart,
  PoundSterling,
  Wrench,
  FileText,
  Coffee,
  Scale,
  ShoppingBag,
  Truck,
  Factory,
  Palette,
  BookOpen,
  Globe,
  Award,
  Clock,
  Target
} from 'lucide-react';
import axios from 'axios';
import AdBlock from '../components/AdBlock';
import { formatMoney } from '../lib/currency';
import { useLocalPrice } from '../lib/localCurrency';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

console.log('DEBUG: API_URL =', API_URL);
console.log('DEBUG: REACT_APP_BACKEND_URL env =', process.env.REACT_APP_BACKEND_URL);

// Helper to safely normalize any value into an array
const toArray = (value) => (Array.isArray(value) ? value : []);
// Icon mapping for sectors
const sectorIcons = {
  technology: Monitor,
  healthcare: Heart,
  finance: PoundSterling,
  education: GraduationCap,
  engineering: Wrench,
  sales: TrendingUp,
  healthcare_social: Users,
  admin: FileText,
  hospitality: Coffee,
  legal: Scale,
  construction: Building2,
  retail: ShoppingBag,
  logistics: Truck,
  manufacturing: Factory,
  creative: Palette,
  hr: Users,
};

const formatCompact = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1)}M+`;
  if (num >= 1000) return `${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}K+`;
  return num.toLocaleString();
};

const formatJoinInterval = (seconds) => {
  if (!seconds) return 'N/A';
  if (seconds < 60) return `${seconds} sec`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${Math.round(seconds / 3600)} hr`;
};

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const localPrice = useLocalPrice(user?.country);
  const money = (gbp) => localPrice.format(gbp, { decimals: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [dashboardStats, setDashboardStats] = useState({
    total_jobs: 0, total_courses: 0, total_companies: 0, total_candidates: 0,
    jobs_last_24h: 0, applications_last_24h: 0, cvs_uploaded_last_7d: 0, candidate_join_interval_sec: null,
  });
  const [sectors, setSectors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [popularCourses, setPopularCourses] = useState([]);
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [activeTab, setActiveTab] = useState('sectors');
  const [homepageCms, setHomepageCms] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryCities, setCountryCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const openCountry = async (country) => {
    setSelectedCountry(country);
    setCountryCities([]);
    setCitiesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/jobs/locations/countries/${encodeURIComponent(country.code)}/cities`);
      setCountryCities(toArray(res.data?.cities));
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    } finally {
      setCitiesLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      axios.get(`${API_URL}/api/jobs/locations/countries`)
        .then((r) => setCountries(toArray(r.data)))
        .catch(() => setCountries([]));
      const [statsRes, sectorsRes, locationsRes, trendingRes, coursesRes, jobsRes, cmsRes] = await Promise.all([
        axios.get(`${API_URL}/api/stats/dashboard`).catch(() => ({ data: { total_jobs: 0, total_courses: 0, total_companies: 0 } })),
        axios.get(`${API_URL}/api/jobs/sectors`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/jobs/locations`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/jobs/trending`).catch(() => ({ data: { searches: [] } })),
        axios.get(`${API_URL}/api/courses/popular?limit=10`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/jobs?featured=true&limit=6`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/content/homepage`).catch(() => ({ data: null })),
      ]);

      setDashboardStats(statsRes.data);
      setSectors(sectorsRes.data);
      setLocations(locationsRes.data);
      setTrendingSearches(trendingRes.data.searches || []);
      setPopularCourses(coursesRes.data);
      setFeaturedJobs(jobsRes.data);
      if (cmsRes.data) setHomepageCms(cmsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (location) params.set('location', location);
    navigate(`/jobs?${params.toString()}`);
  };

  const stats = [
    { value: `${(dashboardStats.total_jobs || 50000).toLocaleString()}+`, label: 'Active Jobs' },
    { value: `${(dashboardStats.total_companies || 10000).toLocaleString()}+`, label: 'Companies' },
    { value: `${(dashboardStats.total_courses || 5000).toLocaleString()}+`, label: 'Courses' },
    { value: '95%', label: 'Success Rate' },
  ];

  const trendingList = toArray(trendingSearches);
  const sectorsList = toArray(sectors);
  const locationsList = toArray(locations);
  const popularCoursesList = toArray(popularCourses);
  const featuredJobsList = toArray(featuredJobs);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#003366] overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920')] bg-cover bg-center opacity-10" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">
              <Sparkles className="w-3 h-3 mr-1.5" />
              {homepageCms?.badge || "UK's Leading Job & Education Platform"}
            </Badge>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
              {homepageCms?.hero_title
                ? homepageCms.hero_title
                : <>Find a job you'll <span className="text-[#E11D48]">enjoy</span></>}
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl">
              {homepageCms?.hero_subtitle || 'Jobs, courses, and career opportunities. Your journey to success starts here.'}
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="mt-8">
              <div className="flex flex-col sm:flex-row gap-3 p-3 bg-white rounded-2xl shadow-2xl">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Job title, keywords, or company"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-12 border-0 shadow-none text-base"
                    data-testid="hero-search-input"
                  />
                </div>
                <div className="flex-1 relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="City or postcode"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="pl-12 h-12 border-0 shadow-none text-base"
                    data-testid="hero-location-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="h-12 px-8 bg-[#E11D48] hover:bg-[#E11D48]/90 text-white rounded-xl text-base font-semibold"
                  data-testid="hero-search-button"
                >
                  Search Jobs
                </Button>
              </div>
            </form>

            {/* Trending Searches */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-white/60 text-sm">Trending:</span>
              {trendingList.slice(0, 5).map((term) => (
                <button
                  key={term}
                  onClick={() => { setSearchQuery(term); }}
                  className="px-3 py-1 text-sm text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                >
                  {term}
                </button>
              ))}
            </div>

            {/* Quick Stats */}
            <div className="mt-8 flex flex-wrap gap-6 text-white/80 text-sm">
              <span>Search {(dashboardStats.total_jobs || 121583).toLocaleString()} new jobs</span>
              <span>•</span>
              <span>{(dashboardStats.jobs_last_24h || 0).toLocaleString()} added in the last 24 hours</span>
            </div>
          </div>
        </div>
      </section>

      {/* <AdBlock position="home-top" showEmpty /> */}

      {/* Stats Section */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[#003366]">{stat.value}</div>
                <div className="text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Jobs Section */}
      <section className="py-12 bg-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-6">Trending jobs</h2>
          
          <div className="flex flex-wrap gap-3 mb-8">
            {trendingList.map((term, idx) => (
              <Link 
                key={idx}
                to={`/jobs?search=${encodeURIComponent(term)}`}
                className="px-4 py-2 bg-white rounded-full border hover:border-[#003366] hover:text-[#003366] transition-colors text-sm font-medium"
              >
                {term} jobs
              </Link>
            ))}
          </div>

          {/* Jobs by Sector/Location Tabs */}
          <Tabs defaultValue="sectors" className="mt-8">
            <TabsList className="bg-white">
              <TabsTrigger value="sectors" className="data-[state=active]:bg-[#003366] data-[state=active]:text-white">
                By Sector
              </TabsTrigger>
              <TabsTrigger value="locations" className="data-[state=active]:bg-[#003366] data-[state=active]:text-white">
                By Location
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="sectors" className="mt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
                {sectorsList.slice(0, 12).map((sector) => {
                  const IconComponent = sectorIcons[sector.id] || Briefcase;
                  return (
                    <Link 
                      key={sector.id}
                      to={`/jobs?sector=${sector.slug}`}
                      className="flex items-center gap-3 p-4 bg-white rounded-xl border hover:border-[#003366] hover:shadow-md transition-all group"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center group-hover:bg-[#003366] transition-colors">
                        <IconComponent className="w-5 h-5 text-[#003366] group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate group-hover:text-[#003366]">{sector.name}</p>
                        <p className="text-xs text-muted-foreground">{sector.job_count?.toLocaleString() || 0} jobs</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
              <div className="mt-4 text-center">
                <Link to="/jobs/sectors" className="text-[#003366] hover:underline text-sm font-medium">
                  View all sectors →
                </Link>
              </div>
            </TabsContent>
            
            <TabsContent value="locations" className="mt-6">
              {selectedCountry && (
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={() => setSelectedCountry(null)}
                    className="text-[#003366] hover:underline text-sm font-medium"
                    data-testid="locations-back-to-countries"
                  >
                    ← All countries
                  </button>
                  <span className="font-semibold text-sm">Cities in {selectedCountry.name}</span>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
                {!selectedCountry && countries.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => openCountry(c)}
                    className="flex items-center gap-3 p-4 bg-white rounded-xl border hover:border-[#003366] hover:shadow-md transition-all group text-left"
                    data-testid={`country-${c.code}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center group-hover:bg-[#003366] transition-colors">
                      <MapPin className="w-5 h-5 text-[#003366] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate group-hover:text-[#003366]">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.job_count?.toLocaleString() || 0} jobs</p>
                    </div>
                  </button>
                ))}
                {!selectedCountry && countries.length === 0 && locationsList.slice(0, 12).map((loc) => (
                  <Link
                    key={loc.id}
                    to={`/jobs?location=${encodeURIComponent(loc.slug || loc.name)}`}
                    className="flex items-center gap-3 p-4 bg-white rounded-xl border hover:border-[#003366] hover:shadow-md transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center group-hover:bg-[#003366] transition-colors">
                      <MapPin className="w-5 h-5 text-[#003366] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate group-hover:text-[#003366]">{loc.name}</p>
                      <p className="text-xs text-muted-foreground">{loc.job_count?.toLocaleString() || 0} jobs</p>
                    </div>
                  </Link>
                ))}
                {selectedCountry && countryCities.slice(0, 24).map((city) => (
                  <Link
                    key={city.id}
                    to={`/jobs?location=${encodeURIComponent(city.name)}&country=${encodeURIComponent(selectedCountry.code)}`}
                    className="flex items-center gap-3 p-4 bg-white rounded-xl border hover:border-[#003366] hover:shadow-md transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center group-hover:bg-[#003366] transition-colors">
                      <MapPin className="w-5 h-5 text-[#003366] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate group-hover:text-[#003366]">{city.name}</p>
                      <p className="text-xs text-muted-foreground">{city.job_count?.toLocaleString() || 0} jobs</p>
                    </div>
                  </Link>
                ))}
              </div>
              {selectedCountry && citiesLoading && (
                <p className="text-center text-sm text-muted-foreground mt-4">Loading cities…</p>
              )}
              {selectedCountry && !citiesLoading && countryCities.length === 0 && (
                <p className="text-center text-sm text-muted-foreground mt-4">No cities available for {selectedCountry.name} yet.</p>
              )}
              <div className="mt-4 text-center">
                <Link to="/jobs/locations" className="text-[#003366] hover:underline text-sm font-medium">
                  View all locations →
                </Link>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Recruiters Directory CTA */}
      <section className="py-12 bg-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
                <div className="w-12 h-12 rounded-xl bg-[#003366]/10 flex items-center justify-center shrink-0">
                  <Users className="w-6 h-6 text-[#003366]" />
                </div>
                <div>
                  <Badge className="bg-[#003366]/10 text-[#003366] mb-2">Recruiters Directory</Badge>
                  <h2 className="text-2xl font-bold mb-2">Find the recruiters behind your next opportunity</h2>
                  <p className="text-muted-foreground">
                    Browse recruiter profiles and discover the jobs they are hiring for.
                  </p>
                </div>
              </div>
              <Link to="/recruiters" className="shrink-0">
                <Button className="bg-[#003366] hover:bg-[#003366]/90 rounded-full px-8">
                  Browse recruiters
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      <AdBlock position="home-middle" showEmpty />

      {/* Popular Courses Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <Badge className="bg-emerald-100 text-emerald-700 mb-2">Courses</Badge>
              <h2 className="text-2xl font-bold text-foreground">Popular courses</h2>
              <p className="text-muted-foreground mt-1">Invest in your future with courses from top institutions</p>
            </div>
            <Link to="/courses">
              <Button variant="outline" className="rounded-full">
                View All Courses
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularCoursesList.slice(0, 4).map((course) => (
              <Card key={course.id} className="group hover:shadow-lg transition-all border-0 shadow-sm">
                <CardContent className="p-0">
                  <div className="h-32 bg-gradient-to-br from-[#003366] to-[#004080] rounded-t-xl flex items-center justify-center">
                    <GraduationCap className="w-12 h-12 text-white/50" />
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-muted-foreground">{course.institution_name}</p>
                    <h3 className="font-semibold text-sm mt-1 line-clamp-2 group-hover:text-[#003366]">
                      {course.title}
                    </h3>
                    <div className="flex items-center justify-between mt-3">
                      <div>
                        {course.is_free ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Free</Badge>
                        ) : (
                          <span className="font-bold text-[#003366]">{formatMoney(course.price, course.currency)}</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {course.duration}
                      </div>
                    </div>
                    <Link to={`/courses/${course.id}`}>
                      <Button size="sm" className="w-full mt-3 bg-[#003366] hover:bg-[#003366]/90">
                        View Course
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Course Banner */}
          <div className="mt-8 p-6 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl text-white flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <BookOpen className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Your next skill, from just {money(19)}</h3>
                <p className="text-white/80">Invest in your career today with thousands of courses</p>
              </div>
            </div>
            <Link to="/courses">
              <Button className="bg-white text-emerald-600 hover:bg-white/90 rounded-full px-8">
                Browse Courses
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Jobs Section */}
      {featuredJobsList.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Featured jobs</h2>
                <p className="text-muted-foreground text-sm mt-1">Handpicked opportunities from top employers</p>
              </div>
              <Link to="/jobs">
                <Button variant="outline" className="rounded-full">
                  See all jobs <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredJobsList.slice(0, 6).map((job) => (
                <Link key={job.id} to={`/jobs/${job.id}`}>
                  <Card className="h-full hover:shadow-md hover:border-[#003366] transition-all border group">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center group-hover:bg-[#003366] transition-colors">
                          <Briefcase className="w-5 h-5 text-[#003366] group-hover:text-white transition-colors" />
                        </div>
                        <div className="flex gap-1">
                          {job.is_featured && <Badge className="bg-amber-100 text-amber-700 text-xs">Featured</Badge>}
                          {job.is_urgent && <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>}
                        </div>
                      </div>
                      <h3 className="font-semibold text-sm group-hover:text-[#003366] line-clamp-2 mb-1">{job.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{job.company_name}</p>
                      <div className="space-y-1.5 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5" />{job.location}
                        </div>
                        {(job.salary_min || job.salary_max) && (
                          <div className="flex items-center gap-1.5">
                            <PoundSterling className="w-3.5 h-3.5" />
                            {job.salary_min && job.salary_max
                              ? `${formatMoney(job.salary_min, job.currency)} – ${formatMoney(job.salary_max, job.currency)}`
                              : job.salary_min ? `From ${formatMoney(job.salary_min, job.currency)}` : `Up to ${formatMoney(job.salary_max, job.currency)}`}
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />{job.job_type?.replace('_', ' ')}
                          {job.remote && <span className="ml-2 text-emerald-600 font-medium">Remote</span>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 4 Income Streams Section — styled like reed.co.uk/recruiter */}
      <section className="py-16 bg-[#003366] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge className="bg-white/10 text-white border-white/20 mb-4">
              <Sparkles className="w-3 h-3 mr-1.5" />
              Our Services
            </Badge>
            <h2 className="text-3xl font-bold"> One integrated platform.</h2>
            <p className="mt-4 text-white/70">
              Whether you're hiring, learning, recruiting, or studying — WorksRun connects you to opportunities that matter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Stream 1: Job Postings */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  {/* <span className="text-xs text-white/50 uppercase tracking-wider">Stream 1</span> */}
                  <h3 className="text-xl font-bold">Job Postings</h3>
                </div>
              </div>
              <p className="text-white/70 mb-4 leading-relaxed">
                Employers post jobs and reach millions of qualified candidates. Free to post with pay-per-application,
                or pay upfront for featured placement and maximum visibility.
              </p>
              <ul className="space-y-2 mb-6 text-sm text-white/60">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400" />Pay-per-application (Free to post)</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400" />Job Advert Credit — from {money(89)}+VAT</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400" />CV Search Database — from {money(99)}+VAT/month</li>
              </ul>
              <Link to="/recruiter">
                <Button className="bg-blue-500 hover:bg-blue-600 rounded-full">
                  Post a Job <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Stream 2: Courses */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  
                  <h3 className="text-xl font-bold">Course Listings</h3>
                </div>
              </div>
              <p className="text-white/70 mb-4 leading-relaxed">
                Universities, colleges, schools and independent trainers list courses to reach learners nationwide.
                Earn revenue from every enrollment through our platform.
              </p>
              <ul className="space-y-2 mb-6 text-sm text-white/60">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" />Open to all accredited institutions</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" />Online, in-person & hybrid delivery</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" />Certificate & accreditation tracking</li>
              </ul>
              <Link to="/courses">
                <Button className="bg-emerald-500 hover:bg-emerald-600 rounded-full">
                  List a Course <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Stream 3: Direct Recruitment */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  
                  <h3 className="text-xl font-bold">Direct Recruitment</h3>
                </div>
              </div>
              <p className="text-white/70 mb-4 leading-relaxed">
                Our expert recruitment team handles the entire hiring process for you. We source, screen and present
                the best candidates — and only charge on a successful placement.
              </p>
              <ul className="space-y-2 mb-6 text-sm text-white/60">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-purple-400" />15% commission on successful placements</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-purple-400" />AI-powered candidate matching</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-purple-400" />No upfront cost — performance based</li>
              </ul>
              <Link to="/recruiter">
                <Button className="bg-purple-500 hover:bg-purple-600 rounded-full">
                  Get Recruitment Help <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Stream 4: Student Services */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  
                  <h3 className="text-xl font-bold">Student Services</h3>
                </div>
              </div>
              <p className="text-white/70 mb-4 leading-relaxed">
                We recruit international students for partner UK universities and assist with the full visa application
                process — Student Tier 4, Graduate, Skilled Worker and beyond.
              </p>
              <ul className="space-y-2 mb-6 text-sm text-white/60">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" />University application support</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" />Student Tier 4 & Graduate visa assistance</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" />Local and international student recruitment</li>
              </ul>
              <Link to="/student-services">
                <Button className="bg-amber-500 hover:bg-amber-600 rounded-full">
                  Explore Programs <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Employers CTA — reed.co.uk/recruiter style */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#003366]/10 text-[#003366] mb-4">For Employers</Badge>
              <h2 className="text-3xl font-bold mb-4">Advertise jobs and find your perfect candidate</h2>
              <p className="text-muted-foreground mb-6">
                Connecting employers with people quickly through the UK's most effective job advert
                and CV Search service. A new candidate joins WorksRun every {formatJoinInterval(dashboardStats.candidate_join_interval_sec)}.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { value: (dashboardStats.cvs_uploaded_last_7d || 0).toLocaleString(), label: 'Unique CVs uploaded/week' },
                  { value: (dashboardStats.applications_last_24h || 0).toLocaleString(), label: 'Applications made daily' },
                  { value: formatCompact(dashboardStats.total_candidates), label: 'Registered candidates' },
                  { value: formatJoinInterval(dashboardStats.candidate_join_interval_sec), label: 'New candidate joins' },
                ].map((s, i) => (
                  <div key={i} className="p-4 rounded-xl bg-[#F0F4F8]">
                    <p className="text-2xl font-bold text-[#003366]">{s.value}</p>
                    <p className="text-sm text-muted-foreground">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/employer/post-job">
                  <Button className="bg-[#E11D48] hover:bg-[#E11D48]/90 rounded-full px-8">
                    Post a Job
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/recruiter">
                  <Button variant="outline" className="rounded-full px-8 border-[#003366] text-[#003366]">
                    View Recruiter Solutions
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Briefcase, title: 'Pay-Per-Application', desc: 'Free to post. Pay only for applications received.', color: 'bg-blue-500' },
                { icon: FileText, title: 'Job Advert Credit', desc: `Premium visibility. From ${money(89)}+VAT per job.`, color: 'bg-purple-500' },
                { icon: Search, title: 'CV Search', desc: `Search our candidate database. From ${money(99)}/month.`, color: 'bg-emerald-500' },
                { icon: Target, title: 'Direct Recruitment', desc: 'We hire for you. 15% fee on placement.', color: 'bg-amber-500' },
              ].map((item, i) => (
                <Card key={i} className="border hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center mb-3`}>
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Support Section */}
      <section className="py-16 bg-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8">Support for you</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Courses Card */}
            <Card className="bg-white border-0 shadow-lg overflow-hidden">
              <CardContent className="p-0">
                <div className="p-8">
                  <Badge className="bg-emerald-100 text-emerald-700 mb-4">Upskill with courses</Badge>
                  <h3 className="text-xl font-bold mb-2">CV Building Masterclass</h3>
                  <p className="text-muted-foreground text-sm mb-4">Reed Courses • Free</p>
                  <ul className="text-sm text-muted-foreground space-y-1 mb-6">
                    <li>• Online</li>
                    <li>• Self paced</li>
                  </ul>
                  <Link to="/courses">
                    <Button className="bg-[#003366] hover:bg-[#003366]/90">View Course</Button>
                  </Link>
                </div>
                <div className="p-6 bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                  <p className="font-medium">Invest in your future with thousands of courses to help you learn and develop.</p>
                  <Link to="/courses" className="mt-2 inline-block text-white/80 hover:text-white underline text-sm">
                    Browse courses →
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Career Advice Card */}
            <Card className="bg-white border-0 shadow-lg overflow-hidden">
              <CardContent className="p-0">
                <div className="p-8">
                  <Badge className="bg-blue-100 text-blue-700 mb-4">Career advice</Badge>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg hover:border-[#003366] transition-colors">
                      <h4 className="font-medium">Free CV template</h4>
                      <Link to="/career-advice/cv-template" className="text-[#003366] text-sm hover:underline">
                        Get Started →
                      </Link>
                    </div>
                    <div className="p-4 border rounded-lg hover:border-[#003366] transition-colors">
                      <h4 className="font-medium">How to: Manage your mental health whilst jobseeking</h4>
                      <Link to="/career-advice" className="text-[#003366] text-sm hover:underline">
                        Get Started →
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-[#003366] text-white">
                  <p className="font-medium">Take your job search to the next level with our expert tips and career advice.</p>
                  <Link to="/career-advice" className="mt-2 inline-block text-white/80 hover:text-white underline text-sm">
                    Browse career advice →
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Job Seekers CTA */}
      <section className="py-16 bg-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-[#003366] to-[#004080] text-white border-0 shadow-lg overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <Badge className="bg-white/10 text-white border-white/20 mb-4">Job Seekers</Badge>
                  <h2 className="text-3xl font-bold mb-4">Looking for work?</h2>
                  <p className="text-white/80 mb-6">
                    Create your profile, upload your CV, and let our AI match you with perfect opportunities across every sector.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/register?type=job_seeker">
                      <Button className="bg-[#E11D48] hover:bg-[#E11D48]/90 rounded-full px-8" data-testid="cta-job-seeker">
                        Start Your Search
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/cv-builder">
                      <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8">
                        Build Your CV
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Sparkles, label: 'AI-powered job matching' },
                    { icon: FileText, label: 'CV scoring & feedback' },
                    { icon: Award, label: 'Interview prep tools' },
                    { icon: TrendingUp, label: 'Salary benchmarking' },
                  ].map((item, i) => (
                    <div key={i} className="bg-white/10 rounded-xl p-4 flex items-center gap-3">
                      <item.icon className="w-5 h-5 text-emerald-300 shrink-0" />
                      <span className="text-sm text-white/80">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <AdBlock position="home-bottom" showEmpty />

      {/* Student Services CTA */}
      <section className="py-16 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white">
              <Badge className="bg-white/20 text-white border-white/30 mb-4">International Students</Badge>
              <h2 className="text-3xl font-bold mb-4">Study in the UK</h2>
              <p className="text-white/80 max-w-xl">
                We partner with top UK universities to help international students achieve their academic dreams. 
                Visa assistance, application support, and career guidance included.
              </p>
            </div>
            <Link to="/student-services">
              <Button className="bg-white text-amber-600 hover:bg-white/90 rounded-full px-8 text-lg h-12">
                <Globe className="w-5 h-5 mr-2" />
                Explore Programs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
